import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination, type FilterCondition } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Button } from '@vnetwork/ui/primitives';
import { useAdminBusinesses, type AdminBusiness } from '@/api/businesses-admin';
import { dataCentralApi } from '@/api/client';
import { paginationView } from '@/pages/customers/pagination-summary';
import { BusinessesAdminStatBand } from './businesses-admin-stat-band';
import { BusinessesAdminToolbar } from './businesses-admin-toolbar';
import { getBusinessAdminColumns } from './businesses-admin-columns';

const PAGE_SIZE = 20;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

function errMsg(err: unknown): string {
  const r = (err as { response?: { data?: { error_description?: string; message?: string } } })?.response;
  return r?.data?.error_description ?? r?.data?.message ?? 'Something went wrong.';
}

/**
 * HRM Businesses admin list — cross-employee trip management (mirrors legacy
 * CÔNG TÁC pageType="hrm"). Stats band → DataTable with leader/BOD approve
 * actions on eligible rows. Row-click → /businesses/:id detail.
 */
export default function BusinessesAdminPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);

  const [leaderTarget, setLeaderTarget] = useState<AdminBusiness | null>(null);
  const [bodTarget, setBodTarget] = useState<AdminBusiness | null>(null);
  const [approving, setApproving] = useState(false);

  const filters = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const c of conditions) {
      if (!c.value) continue;
      out[c.field] = [...(out[c.field] ?? []), c.value];
    }
    return out;
  }, [conditions]);

  const { data, isLoading, isFetching, isError, refetch } = useAdminBusinesses({
    page,
    page_size: PAGE_SIZE,
    sort_by: 'business_id',
    sort_order: 'desc',
    q: query || undefined,
    filters: Object.keys(filters).length ? filters : undefined,
  });

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? rows.length;
  const pageCount = pagination?.total_pages ?? 1;

  async function handleLeaderApprove() {
    if (!leaderTarget) return;
    setApproving(true);
    try {
      await dataCentralApi.put(`/businesses/${leaderTarget.business_id}/lead/approve`, {
        business_leader_feedback: '',
      });
      toast.success('Leader approved');
      refetch();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setApproving(false);
      setLeaderTarget(null);
    }
  }

  async function handleBodApprove() {
    if (!bodTarget) return;
    setApproving(true);
    try {
      await dataCentralApi.put(`/businesses/${bodTarget.business_id}/bod/approve`, {
        business_bod_feedback: '',
      });
      toast.success('BOD approved');
      refetch();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setApproving(false);
      setBodTarget(null);
    }
  }

  const columns = useMemo(
    () => getBusinessAdminColumns({ onLeaderApprove: setLeaderTarget, onBodApprove: setBodTarget }),
    [],
  );

  return (
    <Container size="full">
      <PageHeader
        title="Business Trips"
        description="Admin view of all employee business-trip requests — approve and review."
      />

      <BusinessesAdminStatBand />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'minmax(0, 1fr)' }}>
        <BusinessesAdminToolbar
          query={rawQuery}
          onQueryChange={(v) => { setRawQuery(v); setPage(1); }}
          conditions={conditions}
          filterOpen={filterOpen}
          onFilterOpenChange={setFilterOpen}
          onApplyFilters={(next) => {
            setConditions(next.filter((c) => c.value !== ''));
            setPage(1);
            setFilterOpen(false);
          }}
        />

        {isError ? (
          <Alert
            tone="danger"
            title="Failed to load business trips"
            actions={<Button size="sm" variant="ghost" onClick={() => refetch()}>Retry</Button>}
          />
        ) : isLoading ? (
          <Card flat>
            <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} style={{ height: 24 }} />)}
            </CardBody>
          </Card>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(r) => String(r.business_id)}
            onRowClick={(r) => navigate(`/businesses/${r.business_id}`)}
            emptyState={
              <EmptyState
                icon={<Briefcase size={24} />}
                title="No business trips found"
                description="Try adjusting your search or filters."
              />
            }
            footer={(() => {
              const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
              return (
                <Pagination
                  page={page}
                  pageCount={view.pageCount}
                  onPageChange={setPage}
                  summary={`${view.summary}${isFetching ? ' …' : ''}`}
                />
              );
            })()}
          />
        )}
      </div>

      <ConfirmDialog
        open={leaderTarget !== null}
        onOpenChange={(o) => { if (!o) setLeaderTarget(null); }}
        title="Leader approve"
        description={leaderTarget ? `Approve IBN-${leaderTarget.business_id} for ${leaderTarget.employee_name}?` : ''}
        confirmLabel="Approve"
        loading={approving}
        onConfirm={handleLeaderApprove}
      />

      <ConfirmDialog
        open={bodTarget !== null}
        onOpenChange={(o) => { if (!o) setBodTarget(null); }}
        title="BOD approve"
        description={bodTarget ? `Approve IBN-${bodTarget.business_id} for ${bodTarget.employee_name}?` : ''}
        confirmLabel="Approve"
        loading={approving}
        onConfirm={handleBodApprove}
      />
    </Container>
  );
}
